//calendar.service.ts(frontent)
import { toast } from 'sonner';
import { api } from './api';
import type { ApiResponse } from '../utils/types';
import type { ContentItem, PlatformName, ScheduleStatus } from '../store/calendarStore';

interface FilterOptions {
  searchQuery?: string;
  platform?: PlatformName | string | undefined;
  status?: ScheduleStatus | string | undefined;
  startDate?: string;
  endDate?: string;
}

export interface ContentItemStats {
  total: number;
  published: number;
  overdue: number;
  today: number;
  thisWeek: number;
  byPlatform: Record<string, number>;
  byStatus: Record<string, number>;
  averageSeoScore: number;
}

// Type guard for error with response
function isErrorWithResponse(error: unknown): error is { response?: { status?: number; data?: { message?: string } } } {
  return typeof error === 'object' && error !== null && 'response' in error;
}

export class CalendarService {
  // API endpoints
  private API_ENDPOINTS = {
    calendar: '/calendar',
    calendarItem: (id: string) => `/calendar/${id}`,
    duplicate: (id: string) => `/calendar/${id}/duplicate`,
    reschedule: (id: string) => `/calendar/${id}/reschedule`,
    publish: (id: string) => `/calendar/${id}/publish`,
    bulk: '/calendar/bulk',
    export: '/calendar/export',
    stats: '/calendar/stats',
    templates: '/calendar/templates',
    optimalTimes: (platform: string) => `/calendar/optimal-times/${platform}`,
  };

  // Normalize data
  private transformContentItem(item: any): ContentItem {
    return {
      ...item,
      id: item._id || item.id,
      createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
      updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
      tags: Array.isArray(item.tags) ? item.tags : [],
      checklist: Array.isArray(item.checklist) ? item.checklist : [],
      seoScore: Number(item.seoScore) || 0,
      autoPublish: !!item.autoPublish,
      scheduledDate: new Date(item.scheduledDate),
      publishedAt: item.publishedAt ? new Date(item.publishedAt) : undefined,
    };
  }

  async create(data: Partial<ContentItem>): Promise<ContentItem> {
    try {
      const response = await api.post<ApiResponse<ContentItem>>(this.API_ENDPOINTS.calendar, {
        ...data,
      });
      if (!response.data.success) throw new Error(response.data.message || 'Failed to create content item');
      toast.success('Content item created successfully');
      return this.transformContentItem(response.data.data);
    } catch (error) {
      console.error('Failed to create content item:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to create content item. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async findAll(filters: FilterOptions = {}): Promise<ContentItem[]> {
    try {
      const params = new URLSearchParams();
      if (filters.searchQuery) params.append('q', filters.searchQuery);
      if (filters.platform && filters.platform !== 'all') params.append('platform', filters.platform);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const queryString = params.toString();
      const url = queryString ? `${this.API_ENDPOINTS.calendar}?${queryString}` : this.API_ENDPOINTS.calendar;

      const response = await api.get<ApiResponse<ContentItem[]>>(url);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch content items');
      return response.data.data.map(this.transformContentItem);
    } catch (error) {
      console.error('Failed to fetch content items:', error);
      const message = 'Failed to fetch content items. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async findOne(id: string): Promise<ContentItem | null> {
    try {
      const response = await api.get<ApiResponse<ContentItem>>(this.API_ENDPOINTS.calendarItem(id));
      if (!response.data.success) throw new Error(response.data.message || 'Content item not found');
      return this.transformContentItem(response.data.data);
    } catch (error) {
      console.error('Failed to fetch content item:', error);
      const message = isErrorWithResponse(error) && error.response?.status === 404
        ? 'Content item not found'
        : 'Failed to fetch content item. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async update(id: string, data: Partial<ContentItem>): Promise<ContentItem> {
    try {
      const response = await api.patch<ApiResponse<ContentItem>>(
        this.API_ENDPOINTS.calendarItem(id),
        { ...data, updatedAt: new Date().toISOString() }
      );
      if (!response.data.success) throw new Error(response.data.message || 'Failed to update content item');
      toast.success('Content item updated successfully');
      return this.transformContentItem(response.data.data);
    } catch (error) {
      console.error('Failed to update content item:', error);
      let message = 'Failed to update content item. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Content item not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  async delete(id: string): Promise<void> {
    try {
      const response = await api.delete<ApiResponse<void>>(this.API_ENDPOINTS.calendarItem(id));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to delete content item');
      toast.success('Content item deleted successfully');
    } catch (error) {
      console.error('Failed to delete content item:', error);
      let message = 'Failed to delete content item. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Content item not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  async duplicateContent(id: string, newDate?: Date): Promise<ContentItem> {
    try {
      const response = await api.post<ApiResponse<ContentItem>>(
        this.API_ENDPOINTS.duplicate(id),
        { newDate: newDate?.toISOString() }
      );
      if (!response.data.success) throw new Error(response.data.message || 'Failed to duplicate content item');
      toast.success('Content item duplicated successfully');
      return this.transformContentItem(response.data.data);
    } catch (error) {
      console.error('Failed to duplicate content item:', error);
      const message = 'Failed to duplicate content item. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async bulkUpdateContent(ids: string[], updates: Partial<ContentItem>): Promise<void> {
    try {
      const response = await api.patch<ApiResponse<void>>(
        this.API_ENDPOINTS.bulk,
        { ids, updates: { ...updates, updatedAt: new Date().toISOString() } }
      );
      if (!response.data.success) throw new Error(response.data.message || 'Failed to bulk update content items');
      toast.success('Content items updated successfully');
    } catch (error) {
      console.error('Failed to bulk update content items:', error);
      const message = 'Failed to bulk update content items. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async rescheduleContent(id: string, newDate: Date, newTime?: string): Promise<void> {
    try {
      const response = await api.patch<ApiResponse<void>>(
        this.API_ENDPOINTS.reschedule(id),
        { scheduledDate: newDate.toISOString(), scheduledTime: newTime }
      );
      if (!response.data.success) throw new Error(response.data.message || 'Failed to reschedule content item');
      toast.success('Content item rescheduled successfully');
    } catch (error) {
      console.error('Failed to reschedule content item:', error);
      let message = 'Failed to reschedule content item. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Content item not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  async publishContent(id: string): Promise<Partial<ContentItem>> {
    try {
      const response = await api.patch<ApiResponse<Partial<ContentItem>>>(
        this.API_ENDPOINTS.publish(id)
      );
      if (!response.data.success) throw new Error(response.data.message || 'Failed to publish content item');
      toast.success('Content item published successfully');
      return this.transformContentItem(response.data.data);
    } catch (error) {
      console.error('Failed to publish content item:', error);
      let message = 'Failed to publish content item. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Content item not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  async getContentAnalytics(startDate: Date, endDate: Date): Promise<{
    totalContent: number;
    publishedContent: number;
    engagementRate: number;
    topPlatforms: Array<{ platform: string; count: number }>;
    statusDistribution: Array<{ status: string; count: number }>;
  }> {
    try {
      const response = await api.get<ApiResponse<{
        totalContent: number;
        publishedContent: number;
        engagementRate: number;
        topPlatforms: Array<{ platform: string; count: number }>;
        statusDistribution: Array<{ status: string; count: number }>;
      }>>(`${this.API_ENDPOINTS.calendar}/analytics?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch content analytics');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch content analytics:', error);
      const message = 'Failed to fetch content analytics. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async getContentTemplates(): Promise<Array<{
    id: string;
    name: string;
    platform: string;
    template: Partial<ContentItem>;
  }>> {
    try {
      const response = await api.get<ApiResponse<Array<{
        id: string;
        name: string;
        platform: string;
        template: Partial<ContentItem>;
      }>>>(this.API_ENDPOINTS.templates);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch content templates');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch content templates:', error);
      const message = 'Failed to fetch content templates. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async getOptimalScheduleTimes(platform: string): Promise<string[]> {
    try {
      const response = await api.get<ApiResponse<string[]>>(this.API_ENDPOINTS.optimalTimes(platform));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch optimal schedule times');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch optimal schedule times:', error);
      const message = 'Failed to fetch optimal schedule times. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async getStats(): Promise<ContentItemStats> {
    try {
      const response = await api.get<ApiResponse<ContentItemStats>>(this.API_ENDPOINTS.stats);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch content statistics');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch content statistics:', error);
      const message = 'Failed to fetch content statistics. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async exportContentItems(format: 'csv' | 'json' = 'json'): Promise<Blob> {
    try {
      const response = await api.get(`${this.API_ENDPOINTS.export}?format=${format}`, { responseType: 'blob' });
      toast.success('Content items exported successfully');
      return response.data;
    } catch (error) {
      console.error('Failed to export content items:', error);
      const message = 'Failed to export content items. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async getByPlatform(platform: string): Promise<ContentItem[]> {
    try {
      const response = await api.get<ApiResponse<ContentItem[]>>(
        `${this.API_ENDPOINTS.calendar}?platform=${encodeURIComponent(platform)}`
      );
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch content items by platform');
      return response.data.data.map(this.transformContentItem);
    } catch (error) {
      console.error('Failed to fetch content items by platform:', error);
      const message = 'Failed to fetch content items by platform. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async searchContentItems(query: string): Promise<ContentItem[]> {
    try {
      const response = await api.get<ApiResponse<ContentItem[]>>(
        `${this.API_ENDPOINTS.calendar}?q=${encodeURIComponent(query)}`
      );
      if (!response.data.success) throw new Error(response.data.message || 'Failed to search content items');
      return response.data.data.map(this.transformContentItem);
    } catch (error) {
      console.error('Failed to search content items:', error);
      const message = 'Failed to search content items. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }
}

export const calendarService = new CalendarService();